import Logger, { Level } from './utils/logger';
import EventDispatcher from './utils/eventdispatcher';
import { defaultModel, DEFAULT_VOLUME_STR } from './model';
import {
  requestPreflight,
  requestEntitlement,
  changeBaseUri,
  changeAuth,
  getViewable,
  getPlaybackSettings,
} from './utils/request';
import storage from './utils/storage';
import { extractViewableRights } from './utils/viewable';
import platform, { IS_SAFARI, OS_TYPES } from './utils/platform';
import FullscreenService from './services/fullscreen';
import HeartbeatService from './services/heartbeat';
import AspectratioService from './services/aspectratio';
import TelemetryService from './services/telemetry';
import ErrorhandlingService from './services/errorhandling';
import DAITrackingService from './services/daitracking';
import { SUBTITLES_OFF, CHROMECAST_STATUSES } from '../common/constants';

import ShakaPlayer from './players/shaka';
// load ChromecastSenderService *AFTER* shaka to override shaka's Chromecast callback
import ChromecastSenderService from './services/chromecastsender';

import ChromeCastPlayer from './players/chromecast';
import AdsPlayer from './players/ads';
import { destroyPlayerContainer, domIds, initPlayerContainer } from './dom';
import { getChannelPlayable, has6Channels } from './utils/utils';

export default class Player extends EventDispatcher {
  _mediaTailor = null;

  constructor(playerOptions = {}) {
    super();

    const {
      baseUri,
      chromecast = {},
      airplayEnabled = false,
      detectAdblockers = false,
      logLevel,
      surroundSound = false,
      parentalControl = false,
      parentalControlCheckInterval = 60000,
      muxKey,
      userId,
      partner,
      brandName,
      clientApplicationName,
      clientApplicationVersion,
      fullScreenContainer,
      defaultClosingCreditsOffset,
      disableServices = {},
      type,
    } = playerOptions;

    Logger.setLevel(logLevel || Level.warn);

    this.model = defaultModel()
      .merge({
        chromecast: {
          enabled: chromecast.enabled,
          appId: chromecast.appId,
        },
        airplayEnabled,
        detectAdblockers,
        surroundSound,
        parentalControl,
        parentalControlCheckInterval,
        baseUri,
        muxKey,
        userId,
        partner,
        brandName,
        clientApplicationName,
        clientApplicationVersion,
        defaultClosingCreditsOffset,
        type,
      }, { deep: true });

    this.playerContainer = playerOptions.playerContainer || document.getElementById('player');
    initPlayerContainer(this.playerContainer);

    this.wrapperElement = this.playerContainer.querySelector(`#${domIds.wrapperId}`);
    this.aspectRatioContainer = this.playerContainer.querySelector(`#${domIds.aspectRatioContainerId}`);
    this.videoContainer = this.playerContainer.querySelector(`#${domIds.videoContainerId}`);
    this.playerControlsContainer = this.playerContainer.querySelector(`#${domIds.controlsContainerId}`);
    this.playerElement = this.playerContainer.querySelector(`#${domIds.videoPlayerId}`);
    this.subtitlesElement = this.playerContainer.querySelector(`#${domIds.subtitlesId}`);
    this.adsPlayerElement = this.playerContainer.querySelector(`#${domIds.adsPlayerId}`);
    this.fullScreenContainer = fullScreenContainer || this.playerContainer;

    if (!disableServices.error) this._errorhandlingService = new ErrorhandlingService(this);
    if (!disableServices.fullscreen) this._fullscreenService = new FullscreenService(this);
    if (!disableServices.heartbeat) this._heartbeatService = new HeartbeatService(this);
    if (!disableServices.telemetry) this._telemetryService = new TelemetryService(this);
    if (!disableServices.chromecast) this._chromecastSenderService = new ChromecastSenderService(this);
    if (!disableServices.dai) this._daiTrackingService = new DAITrackingService(this);
    if (!disableServices.aspectratio) this._aspectratioService = new AspectratioService(this);

    changeBaseUri(baseUri);

    if (playerOptions.preferredText) {
      this._savePreferredText(playerOptions.preferredText);
    }
    if (playerOptions.preferredAudio) {
      this._savePreferredAudio(playerOptions.preferredAudio, false);
    }

    this._errorhandlingService?.start();
    this._fullscreenService?.start();
    this._heartbeatService?.start();
    this._aspectratioService?.start();
    this._telemetryService?.start();

    this._shakaPlayer = new ShakaPlayer(this);
    this.addEventListener('ontimeupdate', this._maybeRefreshViewable);
    this.addEventListener('manifestwillupdate', this._onManifestwillupdate);
    this.addEventListener('adsDeactivated', this._onAdsDeactivated);
    this.addEventListener('dai:playing', this._onDaiAdsActivated);
    this.addEventListener('dai:ended', this._onDaiAdsDeactivated);
    this.addEventListener('seeked', this._onSeeked);

    this.currentPlayer = this._shakaPlayer;

    let chromecastPromise = null;
    if (chromecast.enabled) {
      this.addEventListener('chromecastActivated', this._onChromecastActivated);
      this.addEventListener('chromecastDeactivated:before', this._onChromecastDeactivated);
      this.addEventListener('chromecastOntimeupdate', this._maybeRefreshViewable);
      chromecastPromise = this._chromecastSenderService?.start();
    }

    this._initializationPromise = Promise.all([
      chromecastPromise,
      this._shakaPlayer.probeSupport(),
    ]);
  }

  _onSeeked = () => {
    this._heartbeatService?.sendHeartbeat();
  };

  _onChromecastActivated = async () => {
    await this.currentPlayer.unload();
    this._daiTrackingService?.stop(); // Chromecast receiver will track ads
    this.currentPlayer = new ChromeCastPlayer(this);
  };

  _onChromecastDeactivated = async (e) => {
    // If the event was activated and sent when actually chromecasting,
    // we should reload the player otherwise do nothing
    if (!e.detail.wasChromeCasting) {
      return;
    }

    this.currentPlayer = this._shakaPlayer;

    if (this._mediaTailor) {
      // restart Dai service
      const playlist = await this._daiTrackingService?.start(this._mediaTailor.url, this._mediaTailor.params);

      this.model = this.model.merge({
        playlist,
      });
    }

    // Reloading the player from the current point when deactivating chromecasting.
    // Pause Player after reload if Chromecast was in PAUSED state.
    if (!await this._reload()) {
      return;
    }

    if (e && e.detail && e.detail.castPlayerLastState === 'PAUSED') {
      this.pause();
    }
  };

  _exitFullWindow = (area) => {
    if (this._fullscreenService?.isFullScreen()) {
      this._fullscreenService.toggleFullScreen();
    }
    this.dispatchEvent('exitFullWindow', { area });
  };

  _updateEntitlement = async (pinCode, assetIdChanged = false) => {
    const {
      assetId,
      isChromeCasting,
    } = this.model;

    try {
      const { token: entitlementToken } = await requestEntitlement(assetId, pinCode);
      this._updateAuth({ entitlementToken });
      this.dispatchEvent('entitlement');
      return true;
    } catch (e) {
      if (e.code === 403) {
        const reason = JSON.parse(e.extra.response).errors?.[0]?.reason;

        // Chromecast handle pin-code by himself
        if (isChromeCasting && !assetIdChanged) return false;

        const detail = {
          currentTime: this.model.currentTime,
          isLive: this.model.isLive,
        };

        if (reason === 'PINCODE_INCORRECT') {
          this.dispatchEvent('entitlement:incorrect-pincode', detail);
        }

        if (reason === 'PINCODE_NOT_SUBMITTED') {
          this.dispatchEvent('entitlement:required-pincode', detail);
        }

        if (!reason) {
          const error = JSON.parse(e.extra.response)?.error || {};
          if (error.code === 502) {
            throw e;
          }
        }
      }

      if (e.code === 401) {
        throw e;
      }

      Logger.error('failed to update entitlement', e);

      return false;
    }
  };

  _updateAuth = (authOptions) => {
    changeAuth(authOptions);
    this.model = this.model.merge(authOptions);
  };

  _onManifestwillupdate = () => {
    const {
      isPlaying,
    } = this.model;

    if (isPlaying) this._maybeRefreshStream();
  };

  _isRefreshingStream = false;
  _maybeRefreshStream = async () => {
    const {
      assetId,
      currentTime,
      reloadManifest,
      seekRange,
      viewableId,
      metadata,
    } = this.model;

    if (this._isRefreshingStream || !reloadManifest) return;

    const isOutOfSync = seekRange.endAbsolute > reloadManifest;

    if (currentTime > reloadManifest || isOutOfSync) {
      Logger.info('Reaching end of manifest refreshing stream on', currentTime,
        'isOutOfSync', isOutOfSync, 'reloadManifest', reloadManifest);
      try {
        this._isRefreshingStream = true;
        await this.loadWithPreflight({
          playbackStartTime: currentTime,
          assetId,
          viewableId,
          metadata,
        });
      } finally {
        this._isRefreshingStream = false;
      }
    }
  };

  _isRefreshingViewable = false;
  _maybeRefreshViewable = async () => {
    const {
      viewable,
      viewableId,
      assetId,
      currentTime,
    } = this.model;

    const isCatchup = !!viewable?.broadcastById;

    if (this._isRefreshingViewable || !viewableId || !viewable || isCatchup) {
      return;
    }

    // detect moment when channel starts playing new Program and fetch updated viewable
    if (viewable.__typename !== 'Channel') {
      return;
    }

    const isNewerProgam = viewable.schedule?.[0]?.stop <= currentTime;

    if (isNewerProgam) {
      this._isRefreshingViewable = true;

      try {
        await this._updateViewable(viewableId, assetId);
        this.dispatchEvent('viewable:program-changed', {
          viewableId,
          assetId,
        });

        if (this.model.parentalControl) { // check if entitlement is still valid
          const entitled = await this._updateEntitlement();

          if (!entitled) {
            await this.unload();
          }
        }
      } finally {
        this._isRefreshingViewable = false;
      }
    }
  };

  _updateViewable = async (viewableId, playableId) => {
    const response = await getViewable(viewableId, playableId);
    const viewable = response.data.viewer?.viewable;
    this._setViewable(viewable, playableId);
  };

  _setViewable = (viewable, playableId) => {
    this.model = this.model.merge({
      viewable,
      rights: extractViewableRights(viewable, playableId),
      isPlayingDaiAds: false,
    });
    this.dispatchEvent('viewable');
  };

  /**
   * Destroys the current player and recreates a fresh Shaka player instance
   * Networking engine is destroyed first to stop all retries and ongoing requests
   * @private
   */
  _forceResetPlayer = async () => {
    if (this.currentPlayer?.videoPlayer) {
      const networkingEngine = this.currentPlayer.videoPlayer.getNetworkingEngine();
      if (networkingEngine) {
        await networkingEngine.destroy();
      }
      await this.currentPlayer.destroy();
      this._shakaPlayer = new ShakaPlayer(this);
      this.currentPlayer = this._shakaPlayer;
    }
  }

  _preflightInProgress = false;

  getAdParameters = (viewable) => {
    const {
      offsetWidth,
      offsetHeight,
    } = this.adsPlayerElement.parentNode;

    const adParameters = {
      IFA: undefined,
      IFA_TYPE: undefined,
      LMT: undefined,
      APPNAME: this.model.brandName,
      APPBUNDLE: window.location.origin,
      APPSTOREURL: undefined,
      DEVICEMAKE: platform.make,
      DEVICEMODEL: platform.model,
      DEVICETYPE: 'web',
      PLAYERWIDTH: `${offsetWidth}`,
      PLAYERHEIGHT: `${offsetHeight}`,
      USERAGENT: undefined,
      CONTENTTITLE: viewable.title,
      CONTENTSERIES: viewable.show?.title,
      DURATION: `${(viewable.defaultPlayable || viewable.playable)?.duration || -1}`,
      LAT: undefined,
      LON: undefined,
      GDPR: undefined,
      GDPR_CONSENT: undefined,
      US_PRIVACY: undefined,
      COPPA: undefined,
      DNT: navigator.doNotTrack === '1' || navigator.doNotTrack === 'yes' ? '1' : '0',
      IP: undefined,
    };
    return adParameters;
  }

  /**
   * Magine specific way of loading the player via preflight apis
   * @method loadWithPreflight
   * @param  {Object} preflightOptions The data needed for the request, set by the client
   */
  async loadWithPreflight(preflightOptions = {}) {
    await this._initializationPromise;

    const {
      accessToken,
      ads,
      entitlementToken: providedEntitlementToken,
      pinCode,
      authorizationKey,
      playbackStartTime,
      viewableId,
      assetId,
      metadata = {},
      preflightPath,
      viewable,
      preferredBitrate,
      preferredText,
      nextVideo = false,
      disablePlaybackSettings = false,
    } = preflightOptions;

    try {
      const {
        drm,
        protocol,
      } = this.model;
      if (!drm) {
        return;
      }

      const assetIdChanged = assetId !== this.model?.assetId;

      if (this._preflightInProgress) {
        if (!assetIdChanged) {
          Logger.warn('loadWithPreflight: preflight is in progress, ignoring new request');
          return;
        }

        Logger.warn('loadWithPreflight: new asset requested while preflight in progress, force resetting player');
        await this._forceResetPlayer();
      }

      this._preflightInProgress = true;

      // clear state if possible
      if (!this._isRefreshingStream) {
        await this.unload(true, true);
      }

      const getBitrateSetting = () => {
        if (preferredBitrate && !IS_SAFARI) {
          return { preferredBitrate, selectedBitrate: preferredBitrate };
        }
        if ([OS_TYPES.IOS, OS_TYPES.ANDROID].includes(platform.os)) {
          // set quality to auto if is playing from mobile
          return { preferredBitrate: 'auto', selectedBitrate: 'auto' };
        }
        return null;
      };

      this.model = this.model.merge({
        playerError: null,
        assetId,
        viewableId,
        metadata,
        nextVideo,
        ...(getBitrateSetting()),
        ...(preferredText ? { preferredText } : null),
      });

      this._updateAuth({
        accessToken,
        authorizationKey,
      });
      if (providedEntitlementToken) {
        this._updateAuth({ entitlementToken: providedEntitlementToken });
      } else {
        const entitled = await this._updateEntitlement(pinCode, assetIdChanged);

        if (!entitled) { // unload if not entitled to watch the asset (i.e. PIN is invalid)
          await this.unload();
          return;
        }
      }

      if (viewable) {
        // set viewable data that is loaded outside of the player
        this._setViewable(viewable, assetId);
      } else if (viewableId) {
        // we need to know if viewable is Channel before calling .load() https://magine.atlassian.net/browse/MDM-14041
        await this._updateViewable(viewableId, assetId)
          .catch((error) => {
            Logger.warn(`Failed to fetch viewable ${viewableId}:`, error);
          });
      }

      const { hlsManifestOnly, includeAdsParameters } = getChannelPlayable(this.model.viewable);

      if (hlsManifestOnly) {
        this.model = this.model.merge({
          drm: 'none',
          protocol: 'hls',
          originalSupport: { drm, protocol },
        });
      }

      const playInformation = await requestPreflight({
        assetId,
        drm: this.model.drm,
        protocol: this.model.protocol,
        preflightPath,
        parameters: includeAdsParameters ? this.getAdParameters(this.model.viewable) : undefined,
      })
        .catch((e) => {
          if (e.code !== 400 || !e.extra.response) {
            throw e;
          }

          // extract error code from response
          const response = JSON.parse(e.extra.response);

          // eslint-disable-next-line no-throw-literal
          throw ({
            ...e,
            code: response?.error?.code || e.code,
            message: response?.error?.message || e.message,
          });
        });

      const {
        playlist: initialPlaylist,
        license,
        heartbeat,
        certificate,
        headers,
        reloadManifest,
        mediaTailor,
      } = playInformation;

      Logger.warn('manifest url:', initialPlaylist);
      Logger.warn('license url:', license);

      if (!initialPlaylist) {
        throw new Error('preflight failed: playlist url is missing');
      }
      if (!license && this.model.drm !== 'none') {
        throw new Error('preflight failed: license url is missing');
      }
      if (!heartbeat) {
        throw new Error('preflight failed: heartbeat url is missing');
      }

      this._daiTrackingService?.stop(); // stop any pending dai tracking

      if (this.adsPlayer && this.model.adsPlaying) {
        this.adsPlayer.unload();
        this.adsPlayer = null;
      }

      // allow to skip unloading only if need to refresh existing stream
      if (this._isRefreshingStream) {
        await this.unload(!reloadManifest, !reloadManifest);
      }

      // save mediaTailor to restart DaiService after stopping
      this._mediaTailor = mediaTailor;

      let playlist = initialPlaylist;
      if (mediaTailor) {
        playlist = await this._daiTrackingService?.start(mediaTailor.url, mediaTailor.params);
      }

      this.model = this.model.merge({
        license,
        playlist,
        headers,
        heartbeat,
        certificate,
        assetId,
        viewableId,
        metadata,
        reloadManifest,
        playbackStartTime,
      });

      this.dispatchEvent('preflight');

      if (!disablePlaybackSettings) {
        try {
          const response = await getPlaybackSettings();
          const { audioLang, subtitleLang, subtitlesEnabled } = response.data?.viewer?.playbackSettings || {};

          if (audioLang) {
            this._savePreferredAudio(audioLang);
          }
          if (subtitleLang && subtitlesEnabled) {
            this._savePreferredText(subtitleLang);
          }
          if (subtitlesEnabled === false) {
            this._savePreferredText(SUBTITLES_OFF);
          }
        } catch {
          Logger.warn('Cannot load playbackSettings');
        }
      }

      if (this.model.isChromeCasting) {
        this.model = this.model.merge({
          currentTime: playbackStartTime,
        });

        this._onChromecastActivated();

        if (this.model.chromecastPlayableId !== assetId
          || this.model.viewable?.__typename === 'Channel'
          || this._chromecastSenderService?._currentMedia?.playerState === CHROMECAST_STATUSES.IDLE
        ) {
          this._chromecastSenderService?.reloadCastSession();
        }

        // load ads to show them in progress bar
        // and allow to play ads if comeback to the Web player
        if (ads) {
          if (!this.adsPlayer) {
            this.adsPlayer = new AdsPlayer(this);
          }

          try {
            await this.adsPlayer.loadAdsForAsset(assetId);
          } catch (e) {
            Logger.warn('Could not fetch ads', e);
          }
        }

        return;
      }

      if (ads) {
        if (!this.adsPlayer) {
          this.adsPlayer = new AdsPlayer(this);
        }
        if (this.model.detectAdblockers) {
          // stop playback if adblocker detected
          await this.adsPlayer.assertNoAdblock();
        }

        try {
          const adParameters = this.getAdParameters(this.model.viewable);
          await this.adsPlayer.loadAdsForAsset(assetId, adParameters);

          if (this.adsPlayer.isPrerollAvailable()) {
            this.currentPlayer = this.adsPlayer;
            await this.adsPlayer.playPreroll();
            return;
          }
        } catch (e) {
          Logger.warn('Could not fetch any prerolls, skipping', e);
          this.currentPlayer = this._shakaPlayer;
        }
      }

      const normalizedStartTime = playbackStartTime > 0
        ? playbackStartTime
        : null;

      await this._load(this.model.playlist, normalizedStartTime);
    } catch (e) {
      Logger.error('loadWithPreflight failed', e);
      this._errorhandlingService?.onCriticalError(e);
    } finally {
      this._preflightInProgress = false;
    }
  }

  isCasting() {
    const { isChromeCasting, isAirPlaying } = this.model;
    return isChromeCasting || isAirPlaying;
  }

  /**
   * Play a video that is stored out of the Magine
   */
  async playHostedVideo(options = {}) {
    await this._initializationPromise;

    const {
      playbackStartTime,
      viewableId,
      assetId,
      metadata = {},
      viewable,
      nextVideo = false,
      playlist,
    } = options;

    try {
      // clear state if possible
      await this.unload(true, true);

      this.model = this.model.merge({
        playerError: null,
        assetId,
        viewableId,
        metadata,
        nextVideo,
        preferredBitrate: 'auto',
      });

      if (this.adsPlayer && this.model.adsPlaying) {
        this.adsPlayer.unload();
        this.adsPlayer = null;
      }

      this.model = this.model.merge({
        playlist,
        assetId,
        viewableId,
        metadata,
        playbackStartTime,
      });

      if (viewable) {
        // set viewable data that is loaded outside of the player
        this._setViewable(viewable, assetId);
      }

      if (this.model.isChromeCasting) {
        // should be not possible for now
        return;
      }

      const normalizedStartTime = playbackStartTime > 0
        ? playbackStartTime
        : null;

      await this._load(this.model.playlist, normalizedStartTime);
    } catch (e) {
      Logger.error('playHostedVideo', e);
      this._errorhandlingService?.onCriticalError(e);
    }
  }

  _onAdsDeactivated = async (e) => {
    if (!this.model.isChromeCasting) {
      this.currentPlayer = this._shakaPlayer;
    }

    if (e.detail.afterPreroll) {
      /*
        Handles a corny case when we need to reload the player after playing
        preroll but we just switched between a live asset and a vod asset and
        currentTime is still set for the live asset.

        We need to reset
        currentTime in order to be able to play the vod.

        This case happens most
        likely only in our own playground
      */
      this.model = this.model.merge({
        currentTime: this.model.playbackStartTime || 0, // make sure "Continue watching" feature works after preroll
      });

      if (!await this._reload()) {
        return;
      }
    }

    if (e.detail.afterPostroll) {
      return;
    }

    await this.play();
  };

  _onDaiAdsActivated = () => {
    this.model = this.model.merge({
      isPlayingDaiAds: true,
    });
  };

  _onDaiAdsDeactivated = () => {
    this.model = this.model.merge({
      isPlayingDaiAds: false,
    });
  };

  async _load(playlist, playbackStartTime) {
    try {
      this._aspectratioService?.resetSAR();
      await this.currentPlayer.load(playlist, playbackStartTime);
    } catch (e) {
      Logger.error('load failed:', e);

      throw e;
    }
  }

  async _reload() {
    const {
      currentTime,
      playlist,
    } = this.model;

    // possible attempt to load player before preflight request was made
    if (!playlist) {
      return false;
    }

    Logger.info('Attempting reload');
    try {
      await this._load(playlist, currentTime);

      return true;
    } catch (e) {
      Logger.error('Failed to reload', e);
      this._errorhandlingService?.onCriticalError(e);

      return false;
    }
  }

  _saveVolume(volume, saveToStorage = true) {
    const activeVideoVolume = volume || this.model.activeVideoVolume;
    this.model = this.model.merge({
      volume,
      activeVideoVolume,
    });

    if (saveToStorage) {
      storage.setItem('videoVolume', volume);
      storage.setItem('activeVideoVolume', activeVideoVolume);
    }
  }

  _getActiveVideoVolume() {
    return this.model.activeVideoVolume || parseFloat(DEFAULT_VOLUME_STR, 10);
  }

  _savePreferredBitrate(preferredBitrate) {
    this.model = this.model.merge({
      preferredBitrate,
    });

    storage.setItem('bitrate', preferredBitrate);
  }

  _savePreferredAudio(preferredAudio, saveToStorage = true) {
    this.model = this.model.merge({
      preferredAudio,
    });

    if (saveToStorage) {
      storage.setItem('audioTrack', preferredAudio);
    }
  }

  // save surroundAudio. Only in local storage (no backend)
  _savePreferredAudioChannels(surroundAudio = false) {
    storage.setItem('surroundAudio', surroundAudio);
    this.model = this.model.merge({
      surroundAudio,
    });
  }

  _savePreferredText(preferredText) {
    this.model = this.model.merge({
      preferredText,
    });

    storage.setItem('textTrack', preferredText);
  }

  /**
   * Play the video
   */
  async play() {
    this._maybeRefreshStream();
    return this.currentPlayer.play();
  }

  /**
   * Pause play of the video
   */
  async pause() {
    return this.currentPlayer.pause();
  }

  /**
   * Toggle pause/play of the video
   */
  async togglePlay() {
    this._maybeRefreshStream();
    return this.currentPlayer.togglePlay();
  }

  /**
   * Toggle mute/unmute of the video
   */
  async toggleMute() {
    return this.currentPlayer.toggleMute();
  }

  /**
   * Toggle fullscreen mode of the video
   */
  toggleFullScreen() {
    this._fullscreenService?.toggleFullScreen();
  }

  /**
   * Change volume of the video
   */
  async changeVolume(volume) {
    return this.currentPlayer.changeVolume(volume);
  }

  /**
   * Fast forward
   * @param {Number=} jump - Number of seconds to fast forward. 15 by default
   */
  async fastForward(jump = 15) {
    return this.currentPlayer.fastForward(jump);
  }

  /**
   * Rewind
   * @param {Number=} jump - Number of seconds to rewind. 15 by default
   */
  async rewind(jump = 15) {
    return this.currentPlayer.rewind(jump);
  }

  /**
   * Set currentTime of the video
   * @param {Number} time - In seconds
   */
  async seek(time) {
    return this.currentPlayer.seek(time);
  }

  /**
   * Invoke chromecast
   */
  async invokeChromecast() {
    return this._chromecastSenderService?.invokeChromecast();
  }

  /**
   * Invoke airplay
   *
   */
  async invokeAirplay() {
    return this.currentPlayer.invokeAirplay();
  }

  /**
   * Skip ad
   */
  async skip() {
    return this.currentPlayer.skip();
  }

  /**
   * Open ad website
   */
  async openAdWebsite() {
    return this.currentPlayer.openAdWebsite();
  }

  /**
   * Unload the player and reset the model to initial values
   */
  async unload(unloadModel = true, unloadPlayer = true) {
    if (unloadModel) {
      const {
        adBlockOn,
        drm,
        protocol,
        originalSupport,
        isChromeCastAvailable,
        isChromeCasting,
        isAirPlayAvailable,
        isAirPlaying,
        chromecastPlayableId,
        chromecast,
        detectAdblockers,
        surroundSound,
        parentalControl,
        parentalControlCheckInterval,
        baseUri,
        accessToken,
        authorizationKey,
        entitlementToken,
        airplayEnabled,
        muxKey,
        userId,
        partner,
        brandName,
        clientApplicationName,
        clientApplicationVersion,
        defaultClosingCreditsOffset,
        type,
        preferredAudio,
      } = this.model;

      this.model = defaultModel()
        .merge({
          adBlockOn,
          drm: originalSupport.drm || drm,
          protocol: originalSupport.protocol || protocol,
          chromecast,
          isChromeCastAvailable,
          isChromeCasting,
          isAirPlayAvailable,
          isAirPlaying,
          chromecastPlayableId,
          detectAdblockers,
          surroundSound,
          parentalControl,
          parentalControlCheckInterval,
          baseUri,
          accessToken,
          authorizationKey,
          entitlementToken,
          airplayEnabled,
          muxKey,
          userId,
          partner,
          brandName,
          clientApplicationName,
          clientApplicationVersion,
          defaultClosingCreditsOffset,
          type,
        });

      if (preferredAudio) {
        this._savePreferredAudio(preferredAudio, false);
      }

      Logger.debug('Unloading model: ', this.model);
    }

    if (unloadPlayer && this.currentPlayer) {
      this.dispatchEvent('destroy');
      return this.currentPlayer.unload();
    }

    return this.currentPlayer;
  }

  /**
   * Changing text track preference
   * @param {String} language - Language to use
   */
  async changeTextTrack(language) {
    this._savePreferredText(language);
    this.currentPlayer.changeTextTrackPreference(language);
  }

  /**
   * Changing text track position
   * @param {int} position - position of text track
   */
  async changeTextTrackPosition(position) {
    if (this.currentPlayer === this._shakaPlayer) {
      this.currentPlayer.changeTextTrackPosition(position);
    }
  }

  /**
   * Changing bitrate preference
   * @param {String} bitrate - Bitrate to use
   */
  async changeBitrate(bitrate) {
    this._savePreferredBitrate(bitrate);
    this.currentPlayer.changeBitratePreference(bitrate);
  }

  /**
   * Changing audio preference
   * @param {String} language - Language to use
   */
  async changeAudioTrack(language) {
    this._savePreferredAudioChannels(has6Channels(language));
    this._savePreferredAudio(language);
    this.currentPlayer.changeAudioPreference(language);
  }

  /**
   * Wipes the player for completely and clears up memory
   */
  async destroy() {
    this.model = defaultModel();
    this.dispatchEvent('streamEnded');

    this._errorhandlingService?.stop();
    delete this._errorhandlingService;
    this._fullscreenService?.stop();
    delete this._fullscreenService;
    this._heartbeatService?.stop();
    delete this._heartbeatService;
    this._aspectratioService?.stop();
    delete this._aspectratioService;
    this._telemetryService?.stop();
    delete this._telemetryService;
    this._daiTrackingService?.stop();
    delete this._daiTrackingService;
    this._mediaTailor = null;

    this.destroyEventDispatcher();

    await this.currentPlayer.destroy();
    delete this.currentPlayer;
    delete this._shakaPlayer;

    destroyPlayerContainer(this.playerContainer);

    delete this.playerContainer;
    delete this.wrapperElement;
    delete this.aspectRatioContainer;
    delete this.videoContainer;
    delete this.playerControlsContainer;
    delete this.playerElement;
    delete this.subtitlesElement;
    delete this.adsPlayerElement;
    delete this.fullScreenContainer;
  }
}
